import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import Icon from '../AppIcon';
import Button from './Button';

const QRScannerModal = ({ isOpen, onClose, onScanSuccess }) => {
    const scannerRef = useRef(null);
    const [error, setError] = useState(null);
    
    // Use a ref to track mount status safely to avoid stale closure during cleanup
    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        
        if (!isOpen) {
            cleanupScanner();
            return;
        }

        const startScanner = async () => {
            try {
                // Add a small delay to ensure DOM element is ready
                await new Promise(resolve => setTimeout(resolve, 100));
                
                if (!isMounted.current || !isOpen) return;

                setError(null);
                const html5QrCode = new Html5Qrcode("qr-reader");
                scannerRef.current = html5QrCode;

                await html5QrCode.start(
                    { facingMode: "environment" }, // Use back camera on mobile
                    {
                        fps: 10,
                        qrbox: { width: 250, height: 250 }
                    },
                    (decodedText) => {
                        // Clean up the text (in case of weird whitespace from scanner)
                        const cleanText = decodedText?.trim();
                        if (cleanText) {
                            // Successfully scanned QR code
                            handleScanSuccess(cleanText);
                        }
                    },
                    (errorMessage) => {
                        // Scanning error (can be ignored, happens frequently while searching for QR)
                    }
                );
            } catch (err) {
                console.error("Error starting scanner:", err);
                setError("Failed to access camera. Please check camera permissions.");
            }
        };

        startScanner();

        return () => {
            isMounted.current = false;
            cleanupScanner();
        };
    }, [isOpen]);

    const handleScanSuccess = (text) => {
        if (scannerRef.current) {
            try {
                if (scannerRef.current.isScanning) {
                    scannerRef.current.stop().then(() => {
                        scannerRef.current.clear();
                        onScanSuccess(text);
                        onClose();
                    }).catch(err => {
                        console.error("Failed to cleanly stop scanner on success", err);
                        onScanSuccess(text);
                        onClose();
                    });
                    return;
                }
            } catch (e) {
                console.error("Error during scan success cleanup", e);
            }
        }
        onScanSuccess(text);
        onClose();
    };

    const cleanupScanner = () => {
        if (scannerRef.current) {
            try {
                if (scannerRef.current.isScanning) {
                    scannerRef.current.stop().then(() => {
                        scannerRef.current?.clear();
                    }).catch(console.error);
                } else {
                    scannerRef.current.clear();
                }
            } catch (err) {
                console.error("Error during scanner cleanup:", err);
            }
        }
    };

    const handleClose = () => {
        cleanupScanner();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-card w-full max-w-md rounded-xl border border-border shadow-lg overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">Scan QR Code</h2>
                    <button onClick={handleClose} className="p-2 hover:bg-muted rounded-full transition-colors">
                        <Icon name="X" size={20} />
                    </button>
                </div>

                <div className="p-4">
                    {error ? (
                        <div className="p-4 bg-error/10 text-error text-sm rounded-md border border-error/20 mb-4">
                            {error}
                        </div>
                    ) : (
                        <div className="mb-4">
                            <p className="text-sm text-muted-foreground text-center mb-4">
                                Point your camera at a QR code to scan
                            </p>
                        </div>
                    )}

                    <div id="qr-reader" className="w-full rounded-lg overflow-hidden bg-black min-h-[300px] relative">
                         {/* Fallback space for scanner */}
                    </div>

                    <div className="mt-4 flex justify-end">
                        <Button variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QRScannerModal;
